import { useState, useEffect, useRef } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { Box, Typography, CircularProgress, Paper, Button } from '@mui/material'
import { useSnackbar } from 'notistack'
import { useAuth } from '@/contexts/AuthContext'
import { apiClient } from '@/services/apiClient'
import { AcceptInviteResult } from '@/types'

export default function AcceptInvite() {
  const { code } = useParams<{ code: string }>()
  const navigate = useNavigate()
  const { user } = useAuth()
  const { enqueueSnackbar } = useSnackbar()
  const [result, setResult] = useState<AcceptInviteResult | null>(null)
  const [loading, setLoading] = useState(true)
  const acceptingRef = useRef(false)

  useEffect(() => {
    if (!user?.isAuthenticated || !code) {
      setLoading(false)
      return
    }

    if (acceptingRef.current) return
    acceptingRef.current = true

    const accept = async () => {
      try {
        const res = await apiClient.post<AcceptInviteResult>(`/api/invites/accept/${code}`)
        setResult(res)
        if (res.success) {
          enqueueSnackbar(`Joined ${res.entityName}!`, { variant: 'success' })
        }
      } catch (err) {
        enqueueSnackbar(err instanceof Error ? err.message : 'Failed to accept invite', { variant: 'error' })
      } finally {
        setLoading(false)
      }
    }
    accept()
  }, [user, code, enqueueSnackbar])

  if (!user?.isAuthenticated) {
    return (
      <Box sx={{ textAlign: 'center', mt: 8 }}>
        <Paper sx={{ p: 4, maxWidth: 400, mx: 'auto' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
            You've been invited!
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            Please log in or create an account to accept this invitation.
          </Typography>
          <Box sx={{ display: 'flex', gap: 1, justifyContent: 'center' }}>
            <Button variant="contained" onClick={() => navigate('/login', { state: { returnTo: `/invite/${code}` } })}>
              Login
            </Button>
            <Button variant="outlined" onClick={() => navigate('/register', { state: { returnTo: `/invite/${code}` } })}>
              Register
            </Button>
          </Box>
        </Paper>
      </Box>
    )
  }

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}>
        <CircularProgress />
      </Box>
    )
  }

  if (result?.success) {
    const destination = result.entityType === 'group'
      ? `/groups/${result.entityId}`
      : `/families/${result.entityId}`

    return (
      <Box sx={{ textAlign: 'center', mt: 8 }}>
        <Paper sx={{ p: 4, maxWidth: 400, mx: 'auto' }}>
          <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
            Welcome!
          </Typography>
          <Typography color="text.secondary" sx={{ mb: 3 }}>
            You've joined <strong>{result.entityName}</strong>.
          </Typography>
          <Button variant="contained" onClick={() => navigate(destination)}>
            Go to {result.entityType === 'group' ? 'Group' : 'Family'}
          </Button>
        </Paper>
      </Box>
    )
  }

  return (
    <Box sx={{ textAlign: 'center', mt: 8 }}>
      <Paper sx={{ p: 4, maxWidth: 400, mx: 'auto' }}>
        <Typography variant="h5" gutterBottom sx={{ fontWeight: 700 }}>
          Invalid Invite
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {result?.error || 'This invite link is invalid or has expired.'}
        </Typography>
        <Button variant="contained" onClick={() => navigate('/')}>
          Go Home
        </Button>
      </Paper>
    </Box>
  )
}
